import ApiService from '@/core/services/ApiService';
import { Actions, Mutations } from '@/store/enums/StoreEnums';
import { Module, Action, Mutation, VuexModule } from 'vuex-module-decorators';

export interface AdsBrokerageObject {
  id: number;
  areaIds: number[];
  propertyTypeIds: number[];
  sizes: number[];
  sort: number;
  relatedIds: number[];
  isActive: boolean;
  groupLabel?: string;
  groupSlug?: string;
  startingPrice?: number;
  endingPrice?: number;
}

export interface AdsBrokerageConfigObject {
  id?: number;
  brokerName: string;
  agentName: string;
  titleEn: { value: string } | null;
  titleAr: { value: string } | null;
  descriptionEn: { value: string } | null;
  descriptionAr: { value: string } | null;
  phone: string;
  logo: { value: string } | null;
  slug: string;
}

export interface AdsBrokerageListInfo {
  totalItems: number;
  currentPage: number;
  pageSize: number;
  isNext: boolean;
  previous: number;
  adsBrokerages: Array<AdsBrokerageObject>;
  adsBrokerageConfigs: Array<AdsBrokerageConfigObject>;
  errors: unknown;
}

@Module
export default class AdsBrokerageModule
  extends VuexModule
  implements AdsBrokerageListInfo
{
  adsBrokerages = [] as Array<AdsBrokerageObject>;
  listDdlAdsBrokerages = [] as Array<AdsBrokerageObject>;
  adsBrokerageConfigs = [] as Array<AdsBrokerageConfigObject>;
  allAdsBrokerageConfigs = [] as Array<AdsBrokerageConfigObject>;
  totalItems = 0;
  currentPage = 1;
  pageSize = 10;
  previous = 0;
  isNext = true;
  errors = {};

  /**
   * Get Ads Brokerages list
   * @returns Array<AdsBrokerageObject>
   */
  get adsBrokeragesList(): Array<AdsBrokerageObject> {
    return this.adsBrokerages;
  }

  /**
   * Get all Ads Brokerages list
   * @returns Array<AdsBrokerageObject>
   */
  get allListAdsBrokerages(): Array<AdsBrokerageObject> {
    return this.listDdlAdsBrokerages;
  }

  /**
   * Get Ads Brokerage Configs list
   * @returns Array<AdsBrokerageConfigObject>
   */
  get adsBrokerageConfigsList(): Array<AdsBrokerageConfigObject> {
    return this.adsBrokerageConfigs;
  }

  /**
   * Get all Ads Brokerage Configs
   * @returns Array<AdsBrokerageConfigObject>
   */
  get allAdsBrokerageConfigsList(): Array<AdsBrokerageConfigObject> {
    return this.allAdsBrokerageConfigs;
  }

  /**
   * Get total items
   * @returns number
   */
  get adsBrokeragesListCount(): number {
    return this.totalItems;
  }

  @Mutation
  [Mutations.SET_ADS_BROKERAGES](data) {
    this.adsBrokerages = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ADS_BROKERAGE_CONFIGS](data) {
    this.adsBrokerageConfigs = data.data;
    this.totalItems = data.totalItems;
    this.currentPage = data.currentPage;
    this.pageSize = data.pageSize;
    this.isNext = data.isNext;
    this.previous = data.previous;
    this.errors = {};
  }

  @Mutation
  [Mutations.SET_ALL_ADS_BROKERAGE_CONFIGS](data) {
    this.allAdsBrokerageConfigs = data.data || data;
  }

  @Mutation
  [Mutations.SET_ADS_BROKERAGE_CURRENT_PAGE](page) {
    this.currentPage = page;
  }

  @Mutation
  [Mutations.SET_ALL_ADS_BROKERAGES](data) {
    this.listDdlAdsBrokerages = data.data;
  }

  @Mutation
  [Mutations.RESET_ADS_BROKERAGES_LIST]() {
    this.adsBrokerages = [] as Array<AdsBrokerageObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Mutation
  [Mutations.RESET_ADS_BROKERAGE_CONFIGS_LIST]() {
    this.adsBrokerageConfigs = [] as Array<AdsBrokerageConfigObject>;
    this.totalItems = 0;
    this.currentPage = 1;
    this.pageSize = 10;
    this.previous = 0;
    this.isNext = true;
    this.errors = {};
  }

  @Action
  [Actions.RESET_ADS_BROKERAGES_STORE]() {
    this.context.commit(Mutations.RESET_ADS_BROKERAGES_LIST);
  }

  @Action
  [Actions.RESET_ADS_BROKERAGE_CONFIGS_STORE]() {
    this.context.commit(Mutations.RESET_ADS_BROKERAGE_CONFIGS_LIST);
  }

  @Action
  [Actions.CREATE_ADS_BROKERAGE](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/ads-brokerage', data)
      .then(({ data }) => {
        this.context.commit(Mutations.RESET_ADS_BROKERAGES_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.DELETE_ADS_BROKERAGE](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/ads-brokerage', id)
      .then(() => {
        this.context.commit(Mutations.RESET_ADS_BROKERAGES_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_ADS_BROKERAGE]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update(`/ads-brokerage`, id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_ADS_BROKERAGES_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.CREATE_ADS_BROKERAGE_CONFIG](data) {
    ApiService.setAuthorizationHeader();
    return ApiService.post('/ads-brokerage/config', data)
      .then(({ data }) => {
        this.context.commit(Mutations.RESET_ADS_BROKERAGE_CONFIGS_LIST);
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.UPDATE_ADS_BROKERAGE_CONFIG]({ id, data }) {
    ApiService.setAuthorizationHeader();
    return ApiService.update(`/ads-brokerage/config`, id, data)
      .then(() => {
        this.context.commit(Mutations.RESET_ADS_BROKERAGE_CONFIGS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.DELETE_ADS_BROKERAGE_CONFIG](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.delete('/ads-brokerage/config', id)
      .then(() => {
        this.context.commit(Mutations.RESET_ADS_BROKERAGE_CONFIGS_LIST);
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }

  @Action
  [Actions.GET_ADS_BROKERAGE_CONFIG](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/ads-brokerage/config', id);
  }

  @Action
  [Actions.GET_ADS_BROKERAGE_CONFIGS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.query('/ads-brokerage/config/list', {
      params: { page: this.currentPage },
    })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ADS_BROKERAGE_CONFIGS, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.GET_ALL_ADS_BROKERAGE_CONFIGS]() {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/ads-brokerage/config/all')
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ALL_ADS_BROKERAGE_CONFIGS, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.UPDATE_ADS_BROKERAGE_CURRENT_PAGE](val) {
    this.context.commit(Mutations.SET_ADS_BROKERAGE_CURRENT_PAGE, val);
  }

  @Action
  [Actions.GET_ADS_BROKERAGE](id) {
    ApiService.setAuthorizationHeader();
    return ApiService.get('/ads-brokerage', id);
  }

  @Action
  [Actions.GET_ADS_BROKERAGES](filterDto?) {
    ApiService.setAuthorizationHeader();
    const params = {
      page: this.currentPage,
      ...filterDto,
    };
    return ApiService.query('/ads-brokerage', { params })
      .then(({ data }) => {
        this.context.commit(Mutations.SET_ADS_BROKERAGES, data);
      })
      .catch(({ response }) => {
        console.error(response);
      });
  }

  @Action
  [Actions.UPLOAD_ADS_BROKERAGE_LOGO]({ data, id, imageType }) {
    ApiService.setAuthorizationHeader();
    ApiService.setMultipartHeader();
    return ApiService.post(
      `/ads-brokerage/uploadLogo/${id}?imageType=${imageType}`,
      data
    )
      .then(({ data }) => {
        return data.data;
      })
      .catch(({ response }) => {
        this.context.commit(Mutations.SET_ERROR, {
          [response.statusText]: [response.data.message],
        });
      });
  }
}
